// The icon set: one drawing style for every glyph in the staff interface.
//
// Drawn here rather than pulled from a package so there is nothing to install
// and nothing that changes under us. Every icon is a 24-unit square of 1.75
// strokes with round ends, coloured by `currentColor`, so it takes the colour of
// the text it sits beside — white in the sidebar, the accent in a link, red in
// a danger button — without a second set of rules.

import type { ReactNode } from 'react'

const GLYPHS = {
  // ------------------------------------------------------------- sections --
  courses: (
    <>
      <path d="M12 6.5c-1.6-1.3-4.2-2-7.5-2V18c3.3 0 5.9.7 7.5 2 1.6-1.3 4.2-2 7.5-2V4.5c-3.3 0-5.9.7-7.5 2z" />
      <path d="M12 6.5V20" />
    </>
  ),
  students: (
    <>
      <path d="M2.5 9L12 4.5 21.5 9 12 13.5z" />
      <path d="M6.5 11.2v4.3c0 1.4 2.5 3 5.5 3s5.5-1.6 5.5-3v-4.3" />
      <path d="M21.5 9v5" />
    </>
  ),
  submissions: (
    <>
      <path d="M9 4.5h6a.5.5 0 0 1 .5.5v1.5a.5.5 0 0 1-.5.5H9a.5.5 0 0 1-.5-.5V5a.5.5 0 0 1 .5-.5z" />
      <path d="M8.5 5.5h-2A1.5 1.5 0 0 0 5 7v12a1.5 1.5 0 0 0 1.5 1.5h11A1.5 1.5 0 0 0 19 19V7a1.5 1.5 0 0 0-1.5-1.5h-2" />
      <path d="M9 13.5l2 2 4-4.5" />
    </>
  ),
  feedback: (
    <>
      <path d="M5 4.5h14A1.5 1.5 0 0 1 20.5 6v9a1.5 1.5 0 0 1-1.5 1.5h-7l-4.5 3.5v-3.5H5A1.5 1.5 0 0 1 3.5 15V6A1.5 1.5 0 0 1 5 4.5z" />
      <path d="M8 9h8M8 12h5" />
    </>
  ),
  exam: (
    <>
      <path d="M13.5 3.5h-7a1 1 0 0 0-1 1v15a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1V8.5z" />
      <path d="M13.5 3.5v5h5" />
      <path d="M8.5 13l1.5 1.5 3-3" />
      <path d="M8.5 17.5h7" />
    </>
  ),
  members: (
    <>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3.5 19.5c.6-3.2 2.8-5 5.5-5s4.9 1.8 5.5 5" />
      <path d="M15.5 4.9a3.2 3.2 0 0 1 0 6.2" />
      <path d="M17.6 14.7c1.6.7 2.6 2.3 2.9 4.8" />
    </>
  ),
  analytics: (
    <>
      <path d="M4 20h16" />
      <path d="M7 16.5V11M11 16.5V6.5M15 16.5V9.5M19 16.5V13" />
    </>
  ),
  progress: (
    <>
      <path d="M3.5 17l5-5 3.5 3.5 7.5-8" />
      <path d="M15 7.5h4.5V12" />
    </>
  ),
  points: <path d="M12 3.8l2.5 5.1 5.6.8-4 4 .9 5.6-5-2.7-5 2.7.9-5.6-4-4 5.6-.8z" />,
  videos: (
    <>
      <path d="M5.5 5.5h9A1.5 1.5 0 0 1 16 7v10a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 4 17V7a1.5 1.5 0 0 1 1.5-1.5z" />
      <path d="M16 10l4.5-2.5v9L16 14" />
    </>
  ),
  settings: (
    <>
      <path d="M10.3 3.5h3.4l.5 2.3 1.6.9 2.2-.8 1.7 2.9-1.7 1.6v1.8l1.7 1.6-1.7 2.9-2.2-.8-1.6.9-.5 2.3h-3.4l-.5-2.3-1.6-.9-2.2.8-1.7-2.9 1.7-1.6v-1.8L4.3 8.8 6 5.9l2.2.8 1.6-.9z" />
      <circle cx="12" cy="12" r="2.8" />
    </>
  ),
  history: (
    <>
      <path d="M4 12a8 8 0 1 0 2.4-5.7" />
      <path d="M4 4.5V9h4.5" />
      <path d="M12 8v4l2.8 1.8" />
    </>
  ),
  account: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="10" r="3" />
      <path d="M6.6 18.3c1.2-1.9 3.1-3 5.4-3s4.2 1.1 5.4 3" />
    </>
  ),
  logout: (
    <>
      <path d="M9.5 20H6a1.5 1.5 0 0 1-1.5-1.5v-13A1.5 1.5 0 0 1 6 4h3.5" />
      <path d="M15.5 16l4-4-4-4" />
      <path d="M19.5 12h-10" />
    </>
  ),
  home: (
    <>
      <path d="M3.5 11L12 4l8.5 7" />
      <path d="M6 9.5V19a1 1 0 0 0 1 1h3.5v-5.5h3V20H17a1 1 0 0 0 1-1V9.5" />
    </>
  ),
  leaderboard: (
    <>
      <path d="M8 4.5h8v4.5a4 4 0 0 1-8 0z" />
      <path d="M8 6H5v1.5A3 3 0 0 0 8 10.5M16 6h3v1.5a3 3 0 0 1-3 3" />
      <path d="M12 13v3.5" />
      <path d="M8.5 20h7l-.8-3.5H9.3z" />
    </>
  ),
  content: (
    <>
      <path d="M12 4l8.5 4.5L12 13 3.5 8.5z" />
      <path d="M3.5 12.5L12 17l8.5-4.5" />
      <path d="M3.5 16.5L12 21l8.5-4.5" />
    </>
  ),

  // -------------------------------------------------------------- actions --
  upload: (
    <>
      <path d="M12 15.5v-11" />
      <path d="M7.5 9L12 4.5 16.5 9" />
      <path d="M4.5 15v3.5A1.5 1.5 0 0 0 6 20h12a1.5 1.5 0 0 0 1.5-1.5V15" />
    </>
  ),
  download: (
    <>
      <path d="M12 4.5v11" />
      <path d="M7.5 11l4.5 4.5 4.5-4.5" />
      <path d="M4.5 15v3.5A1.5 1.5 0 0 0 6 20h12a1.5 1.5 0 0 0 1.5-1.5V15" />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" />,
  minus: <path d="M5 12h14" />,
  trash: (
    <>
      <path d="M4.5 7h15" />
      <path d="M9.5 7V4.5h5V7" />
      <path d="M6.5 7l.8 11.6A1.5 1.5 0 0 0 8.8 20h6.4a1.5 1.5 0 0 0 1.5-1.4L17.5 7" />
      <path d="M10 11v5.5M14 11v5.5" />
    </>
  ),
  edit: (
    <>
      <path d="M15.5 4.5l4 4-11 11-4.5.5.5-4.5z" />
      <path d="M13.5 6.5l4 4" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="M20 20l-4.3-4.3" />
    </>
  ),
  filter: <path d="M4 5h16l-6.2 7.4v6.1L10.2 20v-7.6z" />,
  check: <path d="M5 12.5l4.5 4.5L19 7.5" />,
  close: <path d="M6 6l12 12M18 6L6 18" />,
  copy: (
    <>
      <path d="M9 8.5h9.5A1.5 1.5 0 0 1 20 10v9.5a1.5 1.5 0 0 1-1.5 1.5H9a1.5 1.5 0 0 1-1.5-1.5V10A1.5 1.5 0 0 1 9 8.5z" />
      <path d="M16.5 8.5V5A1.5 1.5 0 0 0 15 3.5H5.5A1.5 1.5 0 0 0 4 5v9.5A1.5 1.5 0 0 0 5.5 16h2" />
    </>
  ),
  refresh: (
    <>
      <path d="M19.5 12a7.5 7.5 0 1 1-2.2-5.3" />
      <path d="M19.5 4.5v4h-4" />
    </>
  ),
  external: (
    <>
      <path d="M13.5 4.5h6v6" />
      <path d="M19.5 4.5L11 13" />
      <path d="M17 13.5v5a1.5 1.5 0 0 1-1.5 1.5h-10A1.5 1.5 0 0 1 4 18.5v-10A1.5 1.5 0 0 1 5.5 7h5" />
    </>
  ),
  eye: (
    <>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  play: <path d="M8 5.5v13l10.5-6.5z" />,
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
  more: (
    <g fill="currentColor" stroke="none">
      <circle cx="12" cy="5.5" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="12" cy="18.5" r="1.6" />
    </g>
  ),
  sidebar: (
    <>
      <path d="M4.5 4.5h15a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-15a1 1 0 0 1-1-1v-13a1 1 0 0 1 1-1z" />
      <path d="M9.5 4.5v15" />
    </>
  ),

  // ------------------------------------------------------------ direction --
  chevronLeft: <path d="M14.5 6l-6 6 6 6" />,
  chevronRight: <path d="M9.5 6l6 6-6 6" />,
  chevronDown: <path d="M6 9.5l6 6 6-6" />,
  chevronUp: <path d="M6 14.5l6-6 6 6" />,

  // -------------------------------------------------------------- content --
  image: (
    <>
      <path d="M5 4.5h14A1.5 1.5 0 0 1 20.5 6v12a1.5 1.5 0 0 1-1.5 1.5H5A1.5 1.5 0 0 1 3.5 18V6A1.5 1.5 0 0 1 5 4.5z" />
      <circle cx="9" cy="9.5" r="1.8" />
      <path d="M3.5 17l5-5 4 4 2.5-2.5 5.5 5.5" />
    </>
  ),
  video: (
    <>
      <path d="M5 4.5h14A1.5 1.5 0 0 1 20.5 6v12a1.5 1.5 0 0 1-1.5 1.5H5A1.5 1.5 0 0 1 3.5 18V6A1.5 1.5 0 0 1 5 4.5z" />
      <path d="M10 9v6l5-3z" />
    </>
  ),
  audio: (
    <>
      <path d="M4.5 16.5V13a7.5 7.5 0 0 1 15 0v3.5" />
      <path d="M4.5 15h2a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1a1 1 0 0 1-1-1zM19.5 15h-2a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h1a1 1 0 0 0 1-1z" />
    </>
  ),
  file: (
    <>
      <path d="M13.5 3.5h-7a1 1 0 0 0-1 1v15a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1V8.5z" />
      <path d="M13.5 3.5v5h5" />
    </>
  ),
  text: (
    <>
      <path d="M5 7V5h14v2" />
      <path d="M12 5v14" />
      <path d="M9.5 19h5" />
    </>
  ),
  iframe: (
    <>
      <path d="M8.5 8l-4 4 4 4" />
      <path d="M15.5 8l4 4-4 4" />
      <path d="M13 6l-2 12" />
    </>
  ),

  // --------------------------------------------------------------- states --
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </>
  ),
  lock: (
    <>
      <path d="M6 10.5h12a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1z" />
      <path d="M8 10.5v-3a4 4 0 0 1 8 0v3" />
      <path d="M12 14.5V17" />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.5 12h17" />
      <path d="M12 3.5c2.3 2.4 3.4 5.2 3.4 8.5s-1.1 6.1-3.4 8.5c-2.3-2.4-3.4-5.2-3.4-8.5S9.7 5.9 12 3.5z" />
    </>
  ),
  alert: (
    <>
      <path d="M10.7 4.8a1.5 1.5 0 0 1 2.6 0l7.4 12.9a1.5 1.5 0 0 1-1.3 2.3H4.6a1.5 1.5 0 0 1-1.3-2.3z" />
      <path d="M12 9.5v4" />
      <path d="M12 16.8v.2" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 11v5" />
      <path d="M12 7.8v.2" />
    </>
  ),
  phone: (
    <path d="M6.6 3.5h2.8l1.6 4-2.1 1.4a10.5 10.5 0 0 0 6.2 6.2l1.4-2.1 4 1.6v2.8a2 2 0 0 1-2.1 2A15.5 15.5 0 0 1 4.6 5.6a2 2 0 0 1 2-2.1z" />
  ),
  calendar: (
    <>
      <path d="M5 5.5h14a1 1 0 0 1 1 1v12.5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6.5a1 1 0 0 1 1-1z" />
      <path d="M8 3.5v4M16 3.5v4M4 10h16" />
    </>
  ),
} satisfies Record<string, ReactNode>

export type IconName = keyof typeof GLYPHS

/** Every name the set draws, for a screen that lists or picks them. */
export const ICON_NAMES = Object.keys(GLYPHS) as IconName[]

/**
 * An icon. Decorative by default — the text beside it says what it means; pass
 * `label` when the icon stands alone and has to say it itself.
 */
export function Icon({
  name,
  size = 20,
  label,
  className,
}: {
  name: IconName
  size?: number
  label?: string
  className?: string
}) {
  return (
    <svg
      className={className ? `icon ${className}` : 'icon'}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      focusable="false"
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      {GLYPHS[name]}
    </svg>
  )
}
